import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../firebaseConfig';
import QRGenerator, { QRGeneratorHandle } from '../components/QRGenerator';
import QRPosterDesigner from '../components/QRPosterDesigner';
import styles from './QR.module.scss';
import { FaQrcode, FaInfoCircle, FaCopy, FaDownload, FaPrint, FaTrash } from 'react-icons/fa';

type QRType = 'site' | 'public_review';

interface SavedQR {
  id: string;
  name: string;
  type: QRType;
  url: string;
  size: number;
  fgColor: string;
  bgColor: string;
  createdAt: string;
  scanCount: number;        // UI only
  businessId?: string;
}

interface Business {
  id: string;
  name: string;
  website?: string;
}

interface ServerQRDoc {
  _id: string;
  name: string;
  url: string;
  options?: {
    type?: QRType;
    size?: number;
    fgColor?: string;
    bgColor?: string;
    businessId?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

/* -------- API base: normalize REACT_APP_API_URL to always include /api -------- */
const RAW = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const API_BASE = RAW ? (RAW.endsWith('/api') ? RAW : `${RAW}/api`) : '/api';
const apiUrl = (path: string) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

const QR_LIMITS: Record<string, number> = { trial: 5, basic: 20, pro: 100 };

function isValidUrl(url: string) {
  try { new URL(url); return true; } catch { return false; }
}

async function idToken(): Promise<string> {
  const u = auth.currentUser;
  if (!u) throw new Error('Not authenticated');
  return u.getIdToken();
}

const QR: React.FC = () => {
  // --- NEW: Tab (QR vs Poster/Affiche) ---
  const [activeTab, setActiveTab] = useState<'qr' | 'poster'>('qr');

  // Generator state
  const [size, setSize] = useState(180);
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [name, setName] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [savedQRCodes, setSavedQRCodes] = useState<SavedQR[]>([]);
  const [plan, setPlan] = useState<'trial' | 'basic' | 'pro'>('trial');
  const [showTooltip, setShowTooltip] = useState(false);
  const infoRef = useRef<HTMLSpanElement>(null);
  const qrRefs = useRef<{ [id: string]: QRGeneratorHandle | null }>({});

  const [qrType, setQRType] = useState<QRType>('site');

  // Businesses (tenant-scoped)
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [selectedBusinessWebsite, setSelectedBusinessWebsite] = useState('');

  // ----------------------------
  // Helpers: server API calls
  // ----------------------------
  const apiGet = async (path: string) => {
    const token = await idToken();
    const res = await fetch(apiUrl(path), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
    return res.json();
  };

  const apiPost = async (path: string, body: any) => {
    const token = await idToken();
    const res = await fetch(apiUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e?.error || `POST ${path} failed: ${res.status}`);
    }
    return res.json();
  };

  const apiDelete = async (path: string) => {
    const token = await idToken();
    const res = await fetch(apiUrl(path), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`DELETE ${path} failed: ${res.status}`);
    return res.json();
  };

  const serverToLocal = (d: ServerQRDoc): SavedQR => ({
    id: d._id,
    name: d.name,
    type: d.options?.type || 'site',
    url: d.url,
    size: d.options?.size ?? 180,
    fgColor: d.options?.fgColor ?? '#000000',
    bgColor: d.options?.bgColor ?? '#ffffff',
    createdAt: d.createdAt || new Date().toISOString(),
    scanCount: 0,
    businessId: d.options?.businessId,
  });

  // ----------------------------
  // Load plan & businesses (gracefully handle missing /subscription/status)
  // ----------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Try subscription; if 404 or anything fails -> fall back to 'trial'
        let planName: 'trial' | 'basic' | 'pro' = 'trial';
        try {
          const sub = await apiGet('/subscription/status');
          if (!alive) return;
          const p = (sub?.subscription?.plan || 'trial').toLowerCase();
          if (p === 'basic' || p === 'pro' || p === 'trial') {
            planName = p as typeof planName;
          }
        } catch (err) {
          console.warn('subscription/status unavailable — defaulting to trial');
        }
        if (!alive) return;
        setPlan(planName);

        // Always try to load businesses
        try {
          const biz = await apiGet('/my-businesses');
          if (!alive) return;
          const list = (Array.isArray(biz) ? biz : []) as Business[];
          setBusinesses(list);
          if (list.length > 0) {
            setSelectedBusinessId(list[0].id);
            setSelectedBusinessWebsite(list[0].website || '');
          }
        } catch (e: any) {
          if (!alive) return;
          setErrorMsg(e.message || 'Erreur de chargement des établissements.');
        }
      } catch (e: any) {
        if (!alive) return;
        setErrorMsg(e.message || 'Erreur de chargement.');
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update website when business changes
  useEffect(() => {
    const biz = businesses.find(b => b.id === selectedBusinessId);
    setSelectedBusinessWebsite(biz?.website || '');
  }, [selectedBusinessId, businesses]);

  // ----------------------------
  // Load QRs from backend (+ migrate legacy localStorage)
  // ----------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await apiGet('/qrcodes');
        if (!alive) return;
        const items = (data?.items || []) as ServerQRDoc[];
        setSavedQRCodes(items.map(serverToLocal));

        // migrate legacy localStorage -> backend
        const legacy = localStorage.getItem('savedQRCodes');
        if (legacy) {
          const old: SavedQR[] = JSON.parse(legacy);
          if (Array.isArray(old) && old.length) {
            await Promise.allSettled(
              old.map(q =>
                apiPost('/qrcodes', {
                  name: q.name,
                  url: q.url,
                  options: {
                    type: q.type,
                    size: q.size,
                    fgColor: q.fgColor,
                    bgColor: q.bgColor,
                    businessId: q.businessId,
                  },
                })
              )
            );
            const refreshed = await apiGet('/qrcodes');
            if (!alive) return;
            setSavedQRCodes((refreshed?.items || []).map(serverToLocal));
          }
          localStorage.removeItem('savedQRCodes'); // stop cross-account bleed
        }
      } catch (e: any) {
        if (!alive) return;
        setErrorMsg(e.message || 'Impossible de charger les QR codes.');
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------
  // Tooltip
  // ----------------------------
  useEffect(() => {
    if (!showTooltip) return;
    const handler = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) setShowTooltip(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showTooltip]);

  // ----------------------------
  // Derived values
  // ----------------------------
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const qrValue =
    qrType === 'site'
      ? selectedBusinessWebsite
      : qrType === 'public_review'
      ? selectedBusinessId
        ? `${baseUrl}/leave-review/${selectedBusinessId}`
        : ''
      : '';

  const selectedBusinessName =
    businesses.find(b => b.id === selectedBusinessId)?.name || '';

  const valid =
    (qrType === 'site' && selectedBusinessWebsite.length > 0 && isValidUrl(selectedBusinessWebsite)) ||
    (qrType === 'public_review' && selectedBusinessId.length > 0);

  const canAddMore = savedQRCodes.length < QR_LIMITS[plan];

  // ----------------------------
  // Actions
  // ----------------------------
  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) { setErrorMsg('Veuillez sélectionner un établissement et un lien valide.'); setSuccessMsg(''); return; }
    setSuccessMsg('QR Code généré avec succès !'); setErrorMsg('');
  };

  const handleSave = async () => {
    if (!valid) { setErrorMsg('Veuillez sélectionner un établissement et un lien valide.'); setSuccessMsg(''); return; }
    if (!canAddMore) { setErrorMsg('Limite de QR codes atteinte pour votre abonnement.'); setSuccessMsg(''); return; }
    if (!name.trim()) { setErrorMsg('Veuillez donner un nom à votre QR code.'); setSuccessMsg(''); return; }

    try {
      // backend supports flattened payload & recomputes public_review URL
      const payload =
        qrType === 'public_review'
          ? { name: name.trim(), type: 'public_review', businessId: selectedBusinessId, size, fgColor, bgColor }
          : { name: name.trim(), type: 'site', url: qrValue, size, fgColor, bgColor, businessId: selectedBusinessId || undefined };

      const created: ServerQRDoc = await apiPost('/qrcodes', payload);
      const local = serverToLocal(created);
      setSavedQRCodes(prev => [local, ...prev]);
      setSuccessMsg('QR Code sauvegardé !'); setErrorMsg('');
      setName('');
    } catch (e: any) {
      setErrorMsg(e.message || 'Erreur lors de la sauvegarde.');
      setSuccessMsg('');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiDelete(`/qrcodes/${id}`);
      setSavedQRCodes(prev => prev.filter(q => q.id !== id));
    } catch (e: any) {
      setErrorMsg(e.message || 'Suppression impossible.');
    }
  };

  const handleDownloadQR = (id: string) => {
    const qrGen = qrRefs.current[id];
    const canvas = qrGen?.getCanvas();
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = 'qr-code.png';
      a.click();
    }
  };

  const handlePrintQR = (id: string) => {
    const qrGen = qrRefs.current[id];
    const canvas = qrGen?.getCanvas();
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      const win = window.open('');
      if (win) {
        win.document.write(`<img src='${dataUrl}' style='width:300px;display:block;margin:40px auto;'/>`);
        win.print();
        win.close();
      }
    }
  };

  const handleReset = () => {
    setSuccessMsg(''); setErrorMsg('');
    setName('');
    setQRType('site');
    const biz = businesses[0];
    setSelectedBusinessId(biz?.id || '');
    setSelectedBusinessWebsite(biz?.website || '');
  };

  const Icon = (IconComp: any, className = '') => <IconComp className={className} />;

  // Split into two groups & sort newest first
  const siteQRCodes = savedQRCodes
    .filter(qr => qr.type === 'site')
    .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));

  const reviewQRCodes = savedQRCodes
    .filter(qr => qr.type === 'public_review')
    .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));

  // =========================
  // RENDER
  // =========================
  return (
    <main className={styles.qrPageBg}>
      <div className={styles.qrContainerPro}>

        <div className={styles.qrHeaderRow}>
          <h1 className={styles.qrPageTitle}>{Icon(FaQrcode, styles.qrIconTitle)} QR Codes</h1>
          <span
            className={styles.qrHelp}
            ref={infoRef}
            tabIndex={0}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(v => !v)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowTooltip(v => !v)}
            aria-label="Aide QR Codes"
            role="button"
          >
            <FaInfoCircle className={styles.qrHelpIcon} />
            {showTooltip && (
              <div className={styles.qrTooltip} role="tooltip">
                <strong>À propos des QR Codes</strong>
                <div>
                  Générez, personnalisez et gérez vos QR codes pour recueillir des avis clients.<br />
                  <ul className={styles.qrTooltipList}>
                    <li>• Personnalisez la couleur, la taille et le nom.</li>
                    <li>• Sauvegardez, téléchargez, imprimez ou supprimez vos QR codes.</li>
                    <li>• Limite selon votre abonnement.</li>
                    <li>• Deux types : QR pour site ou formulaire public d'avis.</li>
                    <li>• <strong>NOUVEAU</strong> : créez une <em>affiche</em> (logo + QR + CTA) et exportez en PNG/PDF.</li>
                  </ul>
                </div>
              </div>
            )}
          </span>
        </div>

        {/* --- NEW: Tabs --- */}
        <div style={{ display: 'flex', gap: 8, margin: '8px 0 20px' }}>
          <button
            onClick={() => setActiveTab('qr')}
            className={activeTab === 'qr' ? styles.qrButtonSave : styles.qrButton}
            aria-pressed={activeTab === 'qr'}
          >
            Générateur de QR
          </button>
          <button
            onClick={() => setActiveTab('poster')}
            className={activeTab === 'poster' ? styles.qrButtonSave : styles.qrButton}
            aria-pressed={activeTab === 'poster'}
          >
            Affiche (logo + QR)
          </button>
        </div>

        {/* --- Tab content --- */}
        {activeTab === 'qr' ? (
          <>
            <div className={styles.qrLayout}>
              {/* Form */}
              <div className={styles.qrFormCard}>
                <form onSubmit={handleGenerate} className={styles.qrForm} aria-label="QR Code Generator" noValidate>
                  <fieldset className={styles.qrFieldset}>
                    <legend className={styles.qrLegend}>Type de QR Code</legend>
                    <label className={styles.qrRadioLabel}>
                      <input type="radio" name="qrType" value="site" checked={qrType === 'site'} onChange={() => setQRType('site')} />
                      Visiter le site
                    </label>
                    <label className={styles.qrRadioLabel}>
                      <input type="radio" name="qrType" value="public_review" checked={qrType === 'public_review'} onChange={() => setQRType('public_review')} />
                      Formulaire d'Avis Public
                    </label>
                  </fieldset>

                  <fieldset className={styles.qrFieldset}>
                    <legend className={styles.qrLegend}>Sélectionnez l'établissement</legend>
                    <select
                      value={selectedBusinessId}
                      onChange={e => setSelectedBusinessId(e.target.value)}
                      className={styles.qrInput}
                      required
                    >
                      {Array.isArray(businesses) && businesses.length > 0 ? (
                        businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)
                      ) : (
                        <option value="">Aucun établissement</option>
                      )}
                    </select>

                    {qrType === 'site' && (
                      <>
                        <legend className={styles.qrLegend}>Lien du site</legend>
                        <input
                          type="text"
                          value={selectedBusinessWebsite}
                          onChange={e => setSelectedBusinessWebsite(e.target.value)}
                          placeholder="https://votre-site.com"
                          className={styles.qrInput}
                          aria-label="Lien du site"
                          required
                        />
                      </>
                    )}

                    {qrType === 'public_review' && (
                      <div className={styles.qrHelpText}>
                        Ce QR enverra vos clients vers le formulaire d'avis public de cet établissement.
                      </div>
                    )}
                  </fieldset>

                  <fieldset className={styles.qrFieldsett}>
                    <legend className={styles.qrLegend}>Personnalisation</legend>
                    <label className={styles.qrLabel}>
                      Taille
                      <input type="number" min={64} max={512} value={size} onChange={e => setSize(Number(e.target.value))} className={styles.qrInput} aria-label="Taille du QR Code" />
                    </label>
                    <label className={styles.qrLabel}>
                      Couleur
                      <input type="color" value={fgColor} onChange={e => setFgColor(e.target.value)} className={styles.qrInputColor} aria-label="Couleur du QR Code" />
                    </label>
                    <label className={styles.qrLabel}>
                      Fond
                      <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} className={styles.qrInputColor} aria-label="Couleur de fond du QR Code" />
                    </label>
                  </fieldset>

                  <fieldset className={styles.qrFieldset}>
                    <legend className={styles.qrLegend}>Nom du QR Code</legend>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Ex: Accueil, Table 5, Vitrine..."
                      className={styles.qrInput}
                      aria-label="Nom du QR Code"
                      required
                      maxLength={32}
                    />
                  </fieldset>

                  <div className={styles.qrActions}>
                    {/* Row 1 — primary actions */}
                    <div className={styles.qrPrimary}>
                      <button
                        type="submit"
                        className={`${styles.qrButton} ${valid ? styles.qrButtonActive : styles.qrButtonDisabled}`}
                        disabled={!valid}
                        aria-disabled={!valid}
                      >
                        Générer QR Code
                      </button>

                      <button
                        type="button"
                        className={styles.qrButtonSave}
                        onClick={handleSave}
                        disabled={!valid || !name.trim() || !canAddMore}
                      >
                        Sauvegarder
                      </button>
                    </div>

                    {/* Row 2 — secondary actions */}
                    <div className={styles.qrSecondary}>
                      <button type="button" className={styles.qrButtonGhost} onClick={handleReset}>
                        Réinitialiser
                      </button>

                      <button type="button" className={styles.qrButtonGhost} onClick={() => setActiveTab('poster')}>
                        Créer une Affiche
                      </button>
                    </div>
                  </div>

                  {successMsg && <div className={styles.qrSuccess} role="status">{successMsg}</div>}
                  {errorMsg && <div className={styles.qrError} role="alert">{errorMsg}</div>}
                  {!canAddMore && (
                    <div className={styles.qrErrorLimit}>
                      Limite atteinte pour votre abonnement ({QR_LIMITS[plan]} QR codes).
                      <a href="/dashboard/subscription" className={styles.qrUpgradeLink}> Mettre à niveau</a>
                    </div>
                  )}
                </form>
              </div>

              {/* Preview */}
              <div className={styles.qrPreviewSection}>
                <h2 className={styles.qrSectionTitle}>Prévisualisation</h2>
                {qrValue ? (
                  <div className={styles.qrPreviewCard}>
                    <QRGenerator value={qrValue} size={size} fgColor={fgColor} bgColor={bgColor} showDownload />
                    <div className={styles.qrValueLink}>
                      <a href={qrValue} target="_blank" rel="noopener noreferrer">{qrValue}</a>
                    </div>
                  </div>
                ) : (
                  <div className={styles.qrPreviewCardEmpty}>Aucune prévisualisation</div>
                )}
              </div>
            </div>

            {/* Saved lists */}
            <section className={styles.qrListSectionPro}>
              <h2 className={styles.qrSectionTitle}><FaQrcode className={styles.qrIconTitle} /> Mes QR Codes</h2>

              <h3 className={styles.qrGroupTitle}>QR Codes vers site</h3>
              {savedQRCodes.filter(q => q.type === 'site').length === 0 ? (
                <div className={styles.qrEmpty}>Aucun QR code vers site sauvegardé.</div>
              ) : (
                <div className={styles.qrListGridPro}>
                  {siteQRCodes.map(qr => (
                    <div key={qr.id} className={styles.qrCardPro}>
                      <div className={styles.qrCardHeaderPro}>
                        <span className={styles.qrCardName}>{qr.name}</span>
                        <span className={styles.qrCardDate}>{new Date(qr.createdAt).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className={styles.qrCardQrWrap}>
                        <QRGenerator
                          ref={el => { qrRefs.current[qr.id] = el; }}
                          value={qr.url}
                          size={120}
                          fgColor={qr.fgColor}
                          bgColor={qr.bgColor}
                          showDownload={false}
                          className={styles.qrCardPreview}
                        />
                      </div>
                      <div className={styles.qrCardInfoRow}>
                        <span>Scans : <strong>{qr.scanCount}</strong></span>
                        <span>Taille  {qr.size}px</span>
                      </div>
                      <div className={styles.qrCardActionsBar}>
                        <button title="Copier le lien" onClick={() => navigator.clipboard.writeText(qr.url)} className={styles.qrIconBtn}><FaCopy className={styles.qrIcon} /></button>
                        <button title="Télécharger le QR" onClick={() => handleDownloadQR(qr.id)} className={styles.qrIconBtn}><FaDownload className={styles.qrIcon} /></button>
                        <button title="Imprimer le QR" onClick={() => handlePrintQR(qr.id)} className={styles.qrIconBtn}><FaPrint className={styles.qrIcon} /></button>
                        <button title="Supprimer" onClick={() => handleDelete(qr.id)} className={styles.qrIconBtnDanger}><FaTrash className={styles.qrIcon} /></button>
                      </div>
                      {/* quick create poster with this QR */}
                      <div style={{ marginTop: 8, textAlign: 'right' }}>
                        <button
                          className={styles.qrButton}
                          onClick={() => {
                            setActiveTab('poster');
                            if (qr.businessId) setSelectedBusinessId(qr.businessId);
                          }}
                        >
                          Créer une Affiche à partir de ce QR
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <hr className={styles.qrSeparator} />

              <h3 className={styles.qrGroupTitle}>QR Codes vers formulaire d’avis public</h3>
              {savedQRCodes.filter(q => q.type === 'public_review').length === 0 ? (
                <div className={styles.qrEmpty}>Aucun QR code vers formulaire d’avis public sauvegardé.</div>
              ) : (
                <div className={styles.qrListGridPro}>
                  {reviewQRCodes.map(qr => (
                    <div key={qr.id} className={styles.qrCardPro}>
                      <div className={styles.qrCardHeaderPro}>
                        <span className={styles.qrCardName}>{qr.name}</span>
                        <span className={styles.qrCardDate}>{new Date(qr.createdAt).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className={styles.qrCardQrWrap}>
                        <QRGenerator
                          ref={el => { qrRefs.current[qr.id] = el; }}
                          value={qr.url}
                          size={120}
                          fgColor={qr.fgColor}
                          bgColor={qr.bgColor}
                          showDownload={false}
                          className={styles.qrCardPreview}
                        />
                      </div>
                      <div className={styles.qrCardInfoRow}>
                        <span>Scans : <strong>{qr.scanCount}</strong></span>
                        <span>Taille  {qr.size}px</span>
                      </div>
                      <div className={styles.qrCardActionsBar}>
                        <button title="Copier le lien" onClick={() => navigator.clipboard.writeText(qr.url)} className={styles.qrIconBtn}><FaCopy className={styles.qrIcon} /></button>
                        <button title="Télécharger le QR" onClick={() => handleDownloadQR(qr.id)} className={styles.qrIconBtn}><FaDownload className={styles.qrIcon} /></button>
                        <button title="Imprimer le QR" onClick={() => handlePrintQR(qr.id)} className={styles.qrIconBtn}><FaPrint className={styles.qrIcon} /></button>
                        <button title="Supprimer" onClick={() => handleDelete(qr.id)} className={styles.qrIconBtnDanger}><FaTrash className={styles.qrIcon} /></button>
                      </div>
                      {/* quick create poster with this QR */}
                      <div style={{ marginTop: 8, textAlign: 'right' }}>
                        <button
                          className={styles.qrButton}
                          onClick={() => {
                            setActiveTab('poster');
                            if (qr.businessId) setSelectedBusinessId(qr.businessId);
                          }}
                        >
                          Créer une Affiche à partir de ce QR
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        ) : (
          // --- POSTER TAB ---
          <section className={styles.qrPreviewSection}>
            <div style={{ marginBottom: 12, opacity: 0.8 }}>
              Pré-rempli avec <strong>{selectedBusinessName || '—'}</strong> et le lien&nbsp;
              <code>{qrValue || '—'}</code>.<br /> Modifiez le texte, couleurs et logo, puis exportez.
            </div>
            <QRPosterDesigner
              defaultBusinessName={selectedBusinessName}
              defaultQRValue={qrValue}
            />
          </section>
        )}
      </div>
    </main>
  );
};

export default QR;
